import Button from "@mui/material/Button";
import isString from "lodash/isString";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffSsnField from "../../components/finalForm/inputs/RffSsnField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffFormWithFilePreview from "../../components/finalForm/RffFormWithFilePreview";
import RffSubmitButton from "../../components/finalForm/RffSubmitButton";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import Flx from "../../components/layout/Flx";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { cachedDataItemClear } from "../../redux/actions/cachedDataActions";
import RffSelectAutocompleteField from "../../components/finalForm/inputs/RffSelectAutocompleteField";
import { selectOptionsUsaStates } from "../../constants/selectOptions/selectOptionsUsaStates";
import { getUsaStateAbbreviation } from "../../_src_shared/utils/functions/getUsaStateAbbreviation";

const BorrowerCreditAndBackgroundReportForm = ({
  application,
  type, // credit || background
}) => {
  const { loading, pullCreditReportBorrowerPayload } =
    useBorrowerSubmissionsHook();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const dispatch = useDispatch();
  const appId = application?._id;

  const initialValues = useMemo(() => {
    return {
      _id: appId,
      dealId: application?.dealId,
      loanNumber: application?.loanNumber,
      first_name: application?.firstName,
      last_name: application?.lastName,
      ssn: application?.borrowerSSN,
      dateOfBirth: application?.dateOfBirth,
      zip_code: application?.primaryAddress?.zip,
      state: getUsaStateAbbreviation(application?.primaryAddress?.state),
      street_address: `${application?.primaryAddress?.streetNumber} ${application?.primaryAddress?.streetName}`,
      city: application?.primaryAddress?.city,
    };
  }, [application, appId]);

  const onSubmit = (data) => {
    const fileDownloadName =
      isString(data?.first_name) && isString(data?.last_name)
        ? `${data?.first_name}_${data?.last_name}_credit_report.pdf`
        : `credit_report.pdf`;

    if (type === "credit") {
      pullCreditReportBorrowerPayload({
        creditAuthId: appId,
        payload: data,
        fileDownloadName: fileDownloadName,
        onSuccessFn: (resData) => {
          dispatch(cachedDataItemClear("creditAuths"));
          setShowConfirmation(false);
        },
      });
    }
  };

  return (
    <RffFormWithFilePreview
      initialValues={initialValues}
      suppressFooter
      onSubmit={onSubmit}
      formSpy
      loading={loading}
      files={application?.documents}
      sx={{ position: "relative" }}
    >
      <Txt bold sx={{ mb: 2 }}>
        Please review the information below before submitting
      </Txt>
      <RffGroup>
        <RffTextField name="first_name" label="First Name" size={6} />
        <RffTextField name="last_name" label="Last Name" size={6} />
        <RffSsnField name="ssn" label="SSN" size={6} />
        <RffDateField
          name="dateOfBirth"
          label="Enter date of birth"
          required
          size={6}
        />
        <RffTextField name="street_address" label="Street Address" size={12} />
        <RffTextField name="city" label="City" size={6} />
        <RffSelectAutocompleteField
          name="state"
          label="State"
          options={selectOptionsUsaStates}
          size={4}
        />
        <RffTextField name="zip_code" label="Zip Code" size={2} />
        {/* <RffTextField name="state" label="State" size={3} /> */}
      </RffGroup>
      {showConfirmation ? (
        <Flx
          gap={3}
          column
          center
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            background: "#ffffffd1",
            backdropFilter: "blur(5px)",
            width: "100%",
            height: "100%",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <Flx center column gap={3}>
            <Htag>
              Are you sure you want to run a credit report for{" "}
              {application?.firstName} {application?.lastName}?
            </Htag>
            <Flx gap={1}>
              <Button
                size="large"
                color="error"
                onClick={() => setShowConfirmation(false)}
              >
                No, Nevermind
              </Button>
              <RffSubmitButton loading={loading}>
                Yes, Run Credit
              </RffSubmitButton>
            </Flx>
          </Flx>
        </Flx>
      ) : (
        <Flx end gap={1}>
          <Button size="large" onClick={() => setShowConfirmation(true)}>
            Submit
          </Button>
        </Flx>
      )}

      {/* {formSpy ? <JsonPreview values={application} /> : null} */}
    </RffFormWithFilePreview>
  );
};
BorrowerCreditAndBackgroundReportForm.propTypes = {
  sx: PropTypes.object,
  type: PropTypes.oneOf(["credit", "background"]).isRequired,
  children: PropTypes.node.isRequired,
};
export default BorrowerCreditAndBackgroundReportForm;
